<template>
  <MglMap
    :access-token="MAP_ACCESS_TOKEN"
    :map-style="MAP_STYLE"
    :center="INITIAL_CENTER"
    :zoom="initialZoom"
    :attribution-control="false"
    :drag-rotate="false"
    :touch-zoom-rotate="false"
    :pitch-with-rotate="false"
    logo-position="bottom-right"
    class="map"
    @load="initMap"
  >
    <!-- ローディング -->
    <v-overlay
      contained
      :model-value="props.mapLoading"
      class="align-center justify-center"
      style="z-index: 4"
      :persistent="true"
      :no-click-animation="true"
    >
      <LoadingImg />
    </v-overlay>

    <!-- 帰属 -->
    <MglAttributionControl />
    <!-- +/- ズームコントローラー -->
    <MglNavigationControl :show-compass="false" position="top-right" />

    <!-- 縮尺 -->
    <MglScaleControl position="bottom-left" />

    <!-- チェーンポップアップ -->
    <MglPopup
      :showed="hidePopup"
      :coordinates="popupCoordinates"
      anchor="bottom-left"
      :close-button="false"
      :close-on-click="false"
    >
      <div @mousemove="handleMouseMovePopup" @mouseleave="handleMouseLeavePopup">
        <StreetCountPopup
          v-if="popupContent && props.hasStreetOnly"
          :title="`町域名：${popupContent.areaName}`"
          :text="`このエリアに含まれる丁目数：${popupContent.streetCount}`"
          label=""
          :grayed-out="popupContent.streetCount === 0"
          @close-popup="hidePopup = true"
        />
        <BizAreaMapPopup
          v-else-if="popupContent"
          :area-name="popupContent.areaName"
          :chain-name="popupContent.chainName"
          :chain-color="popupContent.color"
          :share="popupContent.visitRatio"
          :on-popup-close="() => (hidePopup = true)"
        />
      </div>
    </MglPopup>
    <HasStreetCheckbox
      v-if="showHasStreetCheckbox"
      :model-value="hasStreetOnly"
      @update:model-value="emits('update:hasStreetOnly', $event)"
    />
  </MglMap>
</template>

<script setup lang="ts">
import {
  MglMap,
  MglPopup,
  MglAttributionControl,
  MglNavigationControl,
  MglScaleControl
} from '@/vendor/vue-mapbox/main'
import { MAP_ACCESS_TOKEN, MAP_STYLE } from '@/config'
import { ref } from 'vue'
import { useStore } from 'vuex'
import { computed } from 'vue'
import {
  COLOR,
  GRANULARITY,
  TOKYO_AREA_ID,
  TOKYO_BBOX_LEFT_DOWN,
  TOKYO_BBOX_RIGHT_TOP
} from '@/commons/enums'
import getDistance from '@/commons/utils/map/get-distance'
import {
  createHatchLayer,
  registerHatchImage,
  STREET_COUNT_NOT_ZERO_FILTER
} from '@/commons/utils/map/hatch-layer'
import { Point } from '@/commons/types/Mapbox'
import LoadingImg from '@/commons/components/loadingImg.vue'
import HasStreetCheckbox from '@/commons/components/BizArea/HasStreetCheckbox.vue'
import { ComparisonMapBoxFeatures } from '../types'
import { watch } from 'vue'
import { processMapData } from '../utils'
import BizAreaMapPopup from './BizAreaMapPopup.vue'
import StreetCountPopup from '@/features/ShopAnalytics/components/BizArea/BizAreaMapPopup.vue'
import { getOverallBbox } from '@/commons/utils/geojson/get-overall-bbox'
import { BBox } from '@/commons/types/GeoJSON'
import { debounce } from 'lodash'

// 都道府県選択時のズームレベル
const PREFECTURE_ZOOM_LEVEL = 4

// 縮尺 (表示領域の縦の距離) がおよそ 40km を示すズームレベル。
// 閾値が 40km なので 40 より少し短めに設定。
const FORTY_KM_ZOOM_LEVEL = 10

// 中心座標の初期値
const INITIAL_CENTER = [137.4103805, 38.15155]

/* --------------------------------------------------------------------------
  props
 ---------------------------------------------------------------------------*/

const emits = defineEmits(['update:hasStreetOnly'])

const props = withDefaults(
  defineProps<{
    /** ローディング */
    mapLoading: boolean
    /** 選択済み都道府県 */
    selectedPrefectureIds: number[]
    /** 基準チェーンID */
    baseChainId: string | undefined
    /** 丁目まで分割可能なエリアに限定するチェックの状態 */
    hasStreetOnly?: boolean
    /** 丁目まで分割可能なエリアに限定するチェックを出すか */
    showHasStreetCheckbox?: boolean
    /** 表示単位 */
    granularity?: (typeof GRANULARITY)[keyof typeof GRANULARITY]
    /** マップに表示するポリゴンデータ */
    featureCollection?: ComparisonMapBoxFeatures[]
  }>(),
  {
    mapLoading: false,
    selectedPrefectureIds: (): number[] => [],
    baseChainId: undefined,
    granularity: GRANULARITY.PREFECTURE,
    featureCollection: () => []
  }
)

/* --------------------------------------------------------------------------
  Vuex
 -------------------------------------------------------------------------- */

const store = useStore()
const chainIdToColorMap = computed<Map<string, string>>(() => store.state.chainIdToColorMap)

/* --------------------------------------------------------------------------
  core
 ---------------------------------------------------------------------------*/

const layerId = 'bizAreaLayerId'
const hatchLayerId = 'hatchBizAreaLayerId'

// Mapbox GI JS Map オブジェクト
// ※ vue-mapbox 側で any 定義されているため any で定義
const map = ref<any>(null)

// bounds の BBox
const bounds = ref<BBox | undefined>()

// ズーム初期値
const initialZoom = ref<number>(
  // 都道府県、もしくはデータ取得前に市区町村・町丁目を選択している際は本土が全域映るズーム倍率
  // (その上で、市区町村・町丁目のときは縮尺アラートが表示される)
  Number(props.granularity) === GRANULARITY.PREFECTURE || !props.featureCollection.length
    ? PREFECTURE_ZOOM_LEVEL
    : FORTY_KM_ZOOM_LEVEL
)

// Map にポリゴンを表示するための source
const geoJsonSource = computed(() => {
  if (props.featureCollection.length === 0) return undefined

  return {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: processMapData(props.featureCollection, chainIdToColorMap.value)
    }
  }
})

// Map にポリゴンを表示するための layer
const geoJsonLayer = computed(() => {
  if (!geoJsonSource.value) return undefined
  return {
    id: layerId,
    type: 'fill',
    source: geoJsonSource.value,
    layout: {},
    paint: {
      'fill-color': [
        'match',
        ['get', 'maxColor'],
        COLOR.RED,
        COLOR.RED,
        COLOR.BLUE,
        COLOR.BLUE,
        COLOR.GREEN,
        COLOR.GREEN,
        COLOR.ORANGE,
        COLOR.ORANGE,
        COLOR.PURPLE,
        COLOR.PURPLE,
        COLOR.BROWN,
        COLOR.BROWN,
        COLOR.GRAY
      ],
      'fill-opacity': ['case', ['has', 'maxColor'], 0.6, 0],
      'fill-outline-color': '#ffffff'
    }
  }
})

const hatchLayer = computed(() => {
  if (!geoJsonSource.value) return undefined
  return createHatchLayer(hatchLayerId, geoJsonSource.value)
})

// 丁目まで分割可能なエリアに限定するチェックの状態を、塗り分けと斜線に反映する
const applyHasStreetOnly = () => {
  if (!map.value) return

  if (map.value.getLayer(layerId)) {
    map.value.setFilter(
      layerId,
      props.hasStreetOnly ? STREET_COUNT_NOT_ZERO_FILTER : ['has', 'maxColor']
    )
  }

  const hasHatchLayer = !!map.value.getLayer(hatchLayerId)
  if (props.hasStreetOnly && !hasHatchLayer && hatchLayer.value) {
    map.value.addLayer(hatchLayer.value)
  } else if (!props.hasStreetOnly && hasHatchLayer) {
    map.value.removeLayer(hatchLayerId)
    map.value.removeSource(hatchLayerId)
  }
}

// ポリゴンを画面内にフィットさせる関数
const fitOverallBounds = (features: ComparisonMapBoxFeatures[]) => {
  if (!map.value) return

  const prefectureIds = props.selectedPrefectureIds.map((id) => id.toString().padStart(2, '0'))

  const bounds = getOverallBbox(
    features.filter(
      (feature: ComparisonMapBoxFeatures) =>
        !!feature.geometry && prefectureIds.includes(feature.properties.areaId.split('-')[0] ?? '')
    )
  )

  if (bounds) map.value.fitBounds(bounds, { padding: 20 })
}

// GeoJson を更新し、ポリゴンレイヤーをアップデートする関数
const updateGeoJson = async () => {
  if (!map.value || props.featureCollection.length === 0) return

  // 既に存在する layer, source を削除
  // addLayer でレイヤーを登録すると、その時の ID で source も登録されるので、layer, source 両方削除する際は登録時の ID で削除する。
  const layerId = geoJsonLayer.value?.id
  const layer = await map.value.getLayer(layerId)

  if (layer) {
    await map.value.removeLayer(layerId)
    await map.value.removeSource(layerId)
  }

  if (map.value.getLayer(hatchLayerId)) {
    map.value.removeLayer(hatchLayerId)
    map.value.removeSource(hatchLayerId)
  }

  if (geoJsonLayer.value) {
    await map.value.addLayer(geoJsonLayer.value)
    applyHasStreetOnly()

    // ポリゴンをフィットさせる
    if (geoJsonSource.value?.data.features) {
      fitOverallBounds(geoJsonSource.value.data.features)
    }

    // エリアホバー時のポップアップを表示する
    map.value.on('mousemove', [layerId, hatchLayerId], (e: any) => {
      handleMouseMoveArea(e)
    })

    // エリアーホバー解除時にポップアップを非表示にする
    map.value.on(
      'mouseleave',
      [layerId, hatchLayerId],
      debounce(() => {
        handleMouseLeaveArea()
        if (!hoveringPopup.value && !hoveringArea.value) {
          handlePopupClose()
        }
      }, 50)
    )
  }
}

// Mapbox GI JS Map オブジェクトを格納するための Load イベントハンドラ関数
const initMap = (event: { map: any; component: any }) => {
  map.value = event.map

  registerHatchImage(map.value)

  if (props.featureCollection.length > 0) {
    updateGeoJson()
  }

  map.value.on('moveend', () => {
    const center: Point = map.value.getCenter()
    const b = map.value.getBounds().toArray()
    bounds.value = {
      xMin: b[0][0],
      xMax: b[1][0],
      yMin: b[0][1],
      yMax: b[1][1]
    }
  })
}

/* --------------------------------------------------------------------------
  ポップアップ関連
 -------------------------------------------------------------------------- */

// ポップアップの開閉状態
// vue-mapbox の仕様上、true で閉じ、false で開く
const hidePopup = ref<boolean>(true)

// 座標 number[] は [経度, 緯度] で格納
const popupCoordinates = ref<number[]>([0, 0])

// ポップアップの表示内容
const popupContent = ref<
  | {
      areaName: string
      chainName: string
      color: string
      visitRatio: number
      streetCount: number
    }
  | undefined
>(undefined)

const hoveringArea = ref(false)
const hoveringPopup = ref(false)

const handleMouseMoveArea = (e: any) => {
  const layers = [layerId, hatchLayerId].filter((id) => map.value.getLayer(id))
  const features = map.value.queryRenderedFeatures(e.point, { layers })

  if (!features.length) return

  const feature = features[0]

  hoveringArea.value = true
  popupContent.value = {
    areaName: props.hasStreetOnly
      ? feature.properties.townName ?? feature.properties.areaName
      : feature.properties.areaName,
    chainName: feature.properties.maxChainName,
    color: feature.properties.maxColor,
    visitRatio: feature.properties.maxVisitRatio,
    streetCount: feature.properties.streetCount ?? 0
  }
  popupCoordinates.value =
    feature.properties.areaId === TOKYO_AREA_ID.toString()
      ? [
          (TOKYO_BBOX_LEFT_DOWN[0] + TOKYO_BBOX_RIGHT_TOP[0]) / 2,
          (TOKYO_BBOX_LEFT_DOWN[1] + TOKYO_BBOX_RIGHT_TOP[1]) / 2
        ]
      : [feature.properties.longitude, feature.properties.latitude]
  hidePopup.value = false
}

const handleMouseLeaveArea = () => {
  hoveringArea.value = false
}

const handleMouseMovePopup = () => {
  hoveringPopup.value = true
}

const handleMouseLeavePopup = () => {
  hoveringPopup.value = false
  handlePopupClose()
}

const handlePopupClose = () => {
  if (!popupContent.value) return

  hidePopup.value = true
  popupCoordinates.value = [0, 0]
  popupContent.value.areaName = ''
  popupContent.value.visitRatio = 0.0
  popupContent.value.streetCount = 0
}

watch(
  () => props.featureCollection,
  () => {
    updateGeoJson()
  },
  { deep: true }
)

watch(
  () => props.hasStreetOnly,
  () => {
    applyHasStreetOnly()
  }
)
</script>

<style scoped>
.map {
  position: relative;
  height: 500px;
  width: 100%;
}

.map-search-bar {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
}

.map-alert {
  position: absolute;
  top: 0px;
  left: 0px;
  bottom: 0px;
  right: 0px;
  margin: auto;
  padding: 0 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 350px;
  height: 83px;
  border: 1px solid var(---666666);
  background: #ffffff 0% 0% no-repeat padding-box;
  border: 1px solid #666666;
  text-align: left;
  font: normal normal normal 13px/26px Noto Sans JP;
  letter-spacing: 0px;
  color: #333333;
  opacity: 0.85;
}

.scale-change-button {
  position: absolute;
  right: 10px;
  bottom: 40px;
}
</style>
